import { formatMoney, formatDate } from "@/lib/format";

const fileUrl = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : null);

// Presentational, read-only formal account statement. Used in the authed preview
// and the public share page. NO signature line anywhere.
export default function StatementDoc({ data }) {
  if (!data) return null;
  const { company = {}, profile = {}, account, statement_date, start, end } = data;
  const header = profile.header_url || company.logo_url;
  const f = (n) => formatMoney(n || 0, "INR");

  return (
    <div className="bg-white mx-auto max-w-3xl p-6 md:p-10 statement-doc" data-testid="statement-doc">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 pb-5 border-b-2 border-indigo-600">
        <div className="flex items-center gap-3">
          {header && <img src={fileUrl(header)} alt="" className="h-14 w-auto max-w-[160px] object-contain" data-testid="statement-header-img" />}
          <div>
            <h1 className="text-2xl font-heading font-extrabold text-slate-900">{company.name}</h1>
            {company.address && <p className="text-xs text-slate-500">{company.address}</p>}
            {company.website && <p className="text-xs text-slate-500">{company.website}</p>}
          </div>
        </div>
        <div className="text-right">
          <p className="text-indigo-700 font-heading font-bold tracking-wide">ACCOUNT STATEMENT</p>
          <p className="text-xs font-semibold text-slate-600 mt-1" data-testid="statement-number">No: {data.statement_number || "Draft"}</p>
          <p className="text-xs text-slate-500 mt-0.5">Date: {formatDate(statement_date)}</p>
          {(start || end) && <p className="text-xs text-slate-500">Period: {start ? formatDate(start) : "—"} → {end ? formatDate(end) : "—"}</p>}
        </div>
      </div>

      {/* Holder + account */}
      <div className="grid grid-cols-2 gap-6 py-5">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Account Holder</p>
          <p className="font-semibold text-slate-800">{profile.holder_name || "—"}</p>
          {profile.holder_address && <p className="text-sm text-slate-500 whitespace-pre-line">{profile.holder_address}</p>}
          <p className="text-sm text-slate-500">A/C: {profile.holder_account_no || "—"}</p>
        </div>
        {account && (
          <div className="text-right">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Bank Account</p>
            <p className="font-semibold text-slate-800">{account.name}</p>
            <p className="text-sm text-slate-500">{account.bank_name}</p>
            <p className="text-sm text-slate-500">{account.account_number}</p>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[["Opening", data.opening], ["Money In", data.total_in], ["Money Out", data.total_out], ["Closing", data.closing]].map(([l, v], i) => (
          <div key={i} className="rounded-xl bg-indigo-50 p-3 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-500">{l}</p>
            <p className={`font-heading font-extrabold mt-0.5 ${l === "Money In" ? "text-emerald-600" : l === "Money Out" ? "text-rose-600" : "text-slate-900"}`}>{f(v)}</p>
          </div>
        ))}
      </div>

      {/* Transactions */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-t border-slate-100">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-white bg-indigo-600">
              <th className="text-left px-3 py-2 font-semibold">Date</th>
              <th className="text-left px-3 py-2 font-semibold">Description</th>
              <th className="text-left px-3 py-2 font-semibold">Category</th>
              <th className="text-right px-3 py-2 font-semibold">Debit</th>
              <th className="text-right px-3 py-2 font-semibold">Credit</th>
              <th className="text-right px-3 py-2 font-semibold">Balance</th>
            </tr>
          </thead>
          <tbody>
            {(data.transactions || []).map((t, i) => (
              <tr key={i} className="border-b border-slate-50 odd:bg-slate-50/50">
                <td className="px-3 py-2 whitespace-nowrap text-slate-600">{formatDate(t.date)}</td>
                <td className="px-3 py-2 text-slate-800">{t.description}</td>
                <td className="px-3 py-2 text-slate-500">{t.category}</td>
                <td className="px-3 py-2 text-right text-rose-600">{t.type === "debit" ? f(t.amount) : ""}</td>
                <td className="px-3 py-2 text-right text-emerald-600">{t.type === "credit" ? f(t.amount) : ""}</td>
                <td className="px-3 py-2 text-right text-slate-600">{t.balance != null ? f(t.balance) : ""}</td>
              </tr>
            ))}
            {(!data.transactions || data.transactions.length === 0) && (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">No transactions in this period</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="pt-5 mt-5 border-t border-slate-100 text-xs text-slate-500 space-y-1">
        {profile.footer_note && <p>{profile.footer_note}</p>}
        {profile.info_email && <p>For queries, contact: <span className="font-semibold text-indigo-600">{profile.info_email}</span></p>}
        <p className="text-slate-400">Generated by SyncPe · System-generated statement, no signature required.</p>
      </div>
    </div>
  );
}
